import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set axios defaults on mount if token exists
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Auth error:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const guestEnter = async () => {
    const response = await axios.get(`${API}/auth/guest-enter`);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, guestEnter, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper functions
export const api = {
  // Departments
  getDepartments: () => axios.get(`${API}/departments`),
  getDepartment: (id) => axios.get(`${API}/departments/${id}`),
  
  // Services
  getServices: (params) => axios.get(`${API}/services`, { params }),
  getService: (id) => axios.get(`${API}/services/${id}`),
  
  // Orders
  getOrders: (params) => axios.get(`${API}/orders`, { params }),
  getOrder: (id) => axios.get(`${API}/orders/${id}`),
  createOrder: (data) => axios.post(`${API}/orders`, data),
  updateOrderStatus: (id, data) => axios.patch(`${API}/orders/${id}/status`, data),
  assignOrder: (id, assignedTo) => axios.patch(`${API}/orders/${id}/assign?assigned_to=${assignedTo}`),
  
  // CRM Connections
  storeCRMCredentials: (data) => axios.post(`${API}/crm/store-credentials`, data),
  createCRMConnectionRequest: (data) => axios.post(`${API}/crm/connection-request`, data),
  getCRMConnections: () => axios.get(`${API}/crm/connections`),
  getCRMConnectionRequests: () => axios.get(`${API}/crm/connection-requests`),
  
  // Order Messages
  getOrderMessages: (orderId) => axios.get(`${API}/orders/${orderId}/messages`),
  addOrderMessage: (orderId, data) => axios.post(`${API}/orders/${orderId}/messages`, data),
  
  // Proposals
  getProposals: () => axios.get(`${API}/proposals`),
  getProposal: (id) => axios.get(`${API}/proposals/${id}`),
  createProposal: (data) => axios.post(`${API}/proposals`, data),
  approveProposal: (id) => axios.post(`${API}/proposals/${id}/approve`),
  
  // Zoom / Expert Sessions
  getZoomRequests: () => axios.get(`${API}/zoom`),
  createZoomRequest: (data) => axios.post(`${API}/zoom`, data),
  confirmZoomRequest: (id, zoomLink) => axios.patch(`${API}/zoom/${id}/confirm?zoom_link=${encodeURIComponent(zoomLink)}`),
  
  // Expert Sessions (paid)
  getExpertSessions: () => axios.get(`${API}/expert-sessions`),
  createExpertSession: (data) => axios.post(`${API}/expert-sessions`, data),
  confirmExpertSession: (id, zoomLink) => axios.patch(`${API}/expert-sessions/${id}/confirm?zoom_link=${encodeURIComponent(zoomLink)}`),
  cancelExpertSession: (id) => axios.patch(`${API}/expert-sessions/${id}/cancel`),
  markSessionPaid: (id) => axios.patch(`${API}/expert-sessions/${id}/mark-paid`),
  markProposalPaid: (id) => axios.patch(`${API}/billing/proposals/${id}/mark-paid`),
  
  // Knowledge Base
  getArticles: (params) => axios.get(`${API}/knowledge/articles`, { params }),
  getArticle: (id) => axios.get(`${API}/knowledge/articles/${id}`),
  getCategories: () => axios.get(`${API}/knowledge/categories`),
  createArticle: (data) => axios.post(`${API}/knowledge/articles`, data),
  
  // AI Chat
  sendChatMessage: (data) => axios.post(`${API}/chat/message`, data),
  
  // Billing
  createCheckout: (proposalId) => axios.post(`${API}/billing/checkout?proposal_id=${proposalId}`),
  getPaymentStatus: (sessionId) => axios.get(`${API}/billing/status/${sessionId}`),
  getTransactions: () => axios.get(`${API}/billing/transactions`),
  
  // Publishing Credits
  getMyCredits: () => axios.get(`${API}/auth/me/credits`),
  getBonusStatus: () => axios.get(`${API}/auth/me/bonus-status`),
  
  // Organization
  getMyOrganization: () => axios.get(`${API}/organizations/me`),
  
  // Admin
  getAdminStats: () => axios.get(`${API}/admin/stats`),
  getUsers: () => axios.get(`${API}/admin/users`),
  updateUserRole: (userId, role) => axios.patch(`${API}/admin/users/${userId}/role?role=${role}`),
  
  // Team Invites
  getTeamInvites: () => axios.get(`${API}/admin/team/invites`),
  sendTeamInvite: (data) => axios.post(`${API}/admin/team/invites`, data),
  resendTeamInvite: (id) => axios.post(`${API}/admin/team/invites/${id}/resend`),
  cancelTeamInvite: (id) => axios.delete(`${API}/admin/team/invites/${id}`),
  createTeamMember: (data) => axios.post(`${API}/admin/team/members`, data),
  
  // Partner Inquiries (Admin)
  getPartnerInquiries: () => axios.get(`${API}/partner-inquiries`),
  updatePartnerInquiryStatus: (id, status) => axios.patch(`${API}/partner-inquiries/${id}/status?status=${status}`),
  
  // Notifications
  getNotifications: () => axios.get(`${API}/notifications`),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`),
  markNotificationRead: (id) => axios.patch(`${API}/notifications/${id}/read`),
  markAllNotificationsRead: () => axios.patch(`${API}/notifications/read-all`),
  
  // Badge System
  getBadgeCode: (businessId) => axios.get(`${API}/badge/code/${businessId}`),
  getBadgeLandingData: (businessId) => axios.get(`${API}/badge/landing/${businessId}`),
  
  // Directory Publishing
  publishBusinessToDirectory: (data) => axios.post(`${API}/directory/my-listing/publish`, data),
  getBusinessPublishStatus: () => axios.get(`${API}/directory/my-listing/publish-status`),
  getBusinessDirectoryPreview: () => axios.get(`${API}/directory/my-listing/preview`),
  publishContentToDirectory: (data) => axios.post(`${API}/directory/content/publish`, data),
  getContentPublishStatus: () => axios.get(`${API}/directory/content/publish-status`),
  unpublishContentFromDirectory: (contentId) => axios.post(`${API}/directory/content/${contentId}/unpublish`),
  pullDirectoryLeads: (params) => axios.get(`${API}/directory/sync/pull-leads`, { params }),
  
  // Gift Portal (Marketing Boost)
  getVacationDestinations: () => axios.get(`${API}/gifts/destinations`),
  sendVacationIncentive: (data) => axios.post(`${API}/gifts/send-vacation`, data),
  sendHotelSavingCard: (data) => axios.post(`${API}/gifts/send-hotel-card`, data),
  sendDiningVoucher: (data) => axios.post(`${API}/gifts/send-dining-voucher`, data),
  getGiftHistory: () => axios.get(`${API}/gifts/history`),
  
  // Service Requests
  getServiceRequests: (params) => axios.get(`${API}/service-requests`, { params }),
  getServiceRequest: (id) => axios.get(`${API}/service-requests/${id}`),
  createServiceRequest: (data) => axios.post(`${API}/service-requests`, data),
  updateServiceRequestStatus: (id, status, adminNotes) => 
    axios.patch(`${API}/service-requests/${id}/status?status=${status}${adminNotes ? `&admin_notes=${encodeURIComponent(adminNotes)}` : ''}`),
  assignServiceRequest: (id, assignedTo) => axios.patch(`${API}/service-requests/${id}/assign?assigned_to=${assignedTo || ''}`),
  
  // Onboarding
  getBrandProfile: () => axios.get(`${API}/onboarding/brand-profile`),
  updateBrandProfile: (data) => axios.put(`${API}/onboarding/brand-profile`, data),
  getOnboardingTasks: () => axios.get(`${API}/onboarding/tasks`),
  updateOnboardingTask: (taskKey, data) => axios.patch(`${API}/onboarding/tasks/${taskKey}`, data),
  getOnboardingProgress: () => axios.get(`${API}/onboarding/progress`),
  
  // DIY Tools
  generateCopy: (data) => axios.post(`${API}/diy/copywriter`, data),
  generateImage: (data) => axios.post(`${API}/diy/image-generator`, data),
  rewriteContent: (data) => axios.post(`${API}/diy/content-rewriter`, data),
  createAppProject: (data) => axios.post(`${API}/diy/app-builder`, data),
  getDIYProjects: () => axios.get(`${API}/diy/projects`),
  getDIYUsage: () => axios.get(`${API}/diy/usage`),
  
  // Best of Best Content
  getBestOfBestContent: (params) => axios.get(`${API}/directory/best-of-best`, { params }),
  createBestOfBestContent: (data) => axios.post(`${API}/directory/best-of-best`, data),
  getBestOfBestItem: (id) => axios.get(`${API}/directory/best-of-best/${id}`),
  likeBestOfBestContent: (id) => axios.post(`${API}/directory/best-of-best/${id}/like`),
  
  // Kling AI (External - removed built-in)
  klingTextToVideo: (data) => axios.post(`${API}/kling/text-to-video`, data),
  klingImageToVideo: (data) => axios.post(`${API}/kling/image-to-video`, data),
  klingTextToImage: (data) => axios.post(`${API}/kling/text-to-image`, data),
  klingLipSync: (data) => axios.post(`${API}/kling/lip-sync`, data),
  klingTTS: (data) => axios.post(`${API}/kling/tts`, data),
  klingTaskStatus: (taskId, taskType = 'video') => axios.get(`${API}/kling/task/${taskId}?task_type=${taskType}`),
  klingVoices: () => axios.get(`${API}/kling/voices`),
  
  // ElevenLabs
  elevenLabsVoices: () => axios.get(`${API}/elevenlabs/voices`),
  elevenLabsTTS: (data) => axios.post(`${API}/elevenlabs/text-to-speech`, data),
  elevenLabsModels: () => axios.get(`${API}/elevenlabs/models`),
  elevenLabsUser: () => axios.get(`${API}/elevenlabs/user`),
  
  // Athena Voice AI
  athenaVoiceChat: (data) => axios.post(`${API}/athena/voice-chat`, data),
  athenaVoices: () => axios.get(`${API}/athena/voices`),
  
  // Generic methods
  post: (url, data) => axios.post(`${BACKEND_URL}${url}`, data),
  get: (url) => axios.get(`${BACKEND_URL}${url}`),
  put: (url, data) => axios.put(`${BACKEND_URL}${url}`, data),
  delete: (url) => axios.delete(`${BACKEND_URL}${url}`),
  
  // File Upload
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Seed Data
  seedData: () => axios.post(`${API}/seed`),
  
  // Ranking System
  getRankingProjects: () => axios.get(`${API}/ranking/projects`),
  getRankingProject: (id) => axios.get(`${API}/ranking/projects/${id}`),
  createRankingProject: (data) => axios.post(`${API}/ranking/projects`, data),
  updateRankingProject: (id, data) => axios.put(`${API}/ranking/projects/${id}`, data),
  deleteRankingProject: (id) => axios.delete(`${API}/ranking/projects/${id}`),
  
  // Ranking Keywords
  getRankingKeywords: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/keywords`),
  addRankingKeyword: (projectId, data) => axios.post(`${API}/ranking/projects/${projectId}/keywords`, data),
  updateRankingKeyword: (projectId, keywordId, data) => axios.put(`${API}/ranking/projects/${projectId}/keywords/${keywordId}`, data),
  deleteRankingKeyword: (projectId, keywordId) => axios.delete(`${API}/ranking/projects/${projectId}/keywords/${keywordId}`),
  
  // Ranking Backlinks
  getRankingBacklinks: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/backlinks`),
  addRankingBacklink: (projectId, data) => axios.post(`${API}/ranking/projects/${projectId}/backlinks`, data),
  deleteRankingBacklink: (projectId, backlinkId) => axios.delete(`${API}/ranking/projects/${projectId}/backlinks/${backlinkId}`),
  
  // Ranking Audits
  getRankingAudits: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/audits`),
  runSiteAudit: (projectId) => axios.post(`${API}/ranking/projects/${projectId}/audits`),
  
  // AI-Powered Ranking Tools
  getAIKeywordSuggestions: (data) => axios.post(`${API}/ranking/ai/keyword-suggestions`, data),
  runAIPageAudit: (data) => axios.post(`${API}/ranking/ai/page-audit`, data),
  generateAdCopy: (data) => axios.post(`${API}/ranking/ai/generate-ad-copy`, data),
  generateContentBrief: (data) => axios.post(`${API}/ranking/ai/content-brief`, data),
  analyzeMetaTags: (data) => axios.post(`${API}/ranking/ai/meta-tags-analyze`, data),
  
  // Tool History
  saveToolHistory: (data) => axios.post(`${API}/ranking/tool-history`, data),
  getToolHistory: (toolType) => axios.get(`${API}/ranking/tool-history${toolType ? `?tool_type=${toolType}` : ''}`),
  getToolHistoryEntry: (entryId) => axios.get(`${API}/ranking/tool-history/${entryId}`),
  deleteToolHistoryEntry: (entryId) => axios.delete(`${API}/ranking/tool-history/${entryId}`),
  
  // Google Ads Campaigns
  getRankingCampaigns: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/campaigns`),
  createRankingCampaign: (projectId, data) => axios.post(`${API}/ranking/projects/${projectId}/campaigns`, data),
  updateCampaignPerformance: (projectId, campaignId, performance) => axios.put(`${API}/ranking/projects/${projectId}/campaigns/${campaignId}/performance`, performance),
  
  // Content Calendar
  getRankingContent: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/content`),
  createRankingContent: (projectId, data) => axios.post(`${API}/ranking/projects/${projectId}/content`, data),
  updateRankingContent: (projectId, contentId, data) => axios.put(`${API}/ranking/projects/${projectId}/content/${contentId}`, data),
  
  // SOPs
  getSOPs: (module) => axios.get(`${API}/ranking/sops${module ? `?module=${module}` : ''}`),
  getSOPProgress: (projectId) => axios.get(`${API}/ranking/projects/${projectId}/sop-progress`),
  updateSOPProgress: (projectId, sopId, stepNumber, itemIndex, completed) => 
    axios.post(`${API}/ranking/projects/${projectId}/sop-progress?sop_id=${sopId}&step_number=${stepNumber}&item_index=${itemIndex}&completed=${completed}`),
  
  // CRM Audit Integration
  runCRMAudit: (data) => axios.post(`${API}/ranking/crm-audit`, data),
  getCRMAuditStatus: () => axios.get(`${API}/ranking/crm-audit-status`),
  
  // Done For You (DFY) Projects
  getDFYPricing: () => axios.get(`${API}/dfy/pricing`),
  getDFYProjects: (params) => axios.get(`${API}/dfy/projects`, { params }),
  getDFYProject: (id) => axios.get(`${API}/dfy/projects/${id}`),
  createDFYProject: (data) => axios.post(`${API}/dfy/projects`, data),
  updateDFYProject: (id, data) => axios.put(`${API}/dfy/projects/${id}`, data),
  deleteDFYProject: (id) => axios.delete(`${API}/dfy/projects/${id}`),
  updateDFYOnboarding: (id, data) => axios.put(`${API}/dfy/projects/${id}/onboarding`, data),
  addDFYKeyword: (projectId, keyword) => axios.post(`${API}/dfy/projects/${projectId}/keywords?keyword=${encodeURIComponent(keyword)}`),
  deleteDFYKeyword: (projectId, keywordId) => axios.delete(`${API}/dfy/projects/${projectId}/keywords/${keywordId}`),
  addDFYContent: (projectId, title, contentType) => axios.post(`${API}/dfy/projects/${projectId}/content?title=${encodeURIComponent(title)}&content_type=${contentType}`),
  updateDFYContent: (projectId, contentId, status) => axios.put(`${API}/dfy/projects/${projectId}/content/${contentId}?status=${status}`),
  generateDFYReport: (projectId, data) => axios.post(`${API}/dfy/projects/${projectId}/reports/generate`, null, { params: data }),
  getDemoProject: () => axios.get(`${API}/dfy/demo-project`),
  createDFYCheckout: (projectId) => axios.post(`${API}/dfy/projects/${projectId}/checkout`),
  getDFYPaymentStatus: (projectId, sessionId) => axios.get(`${API}/dfy/projects/${projectId}/payment-status/${sessionId}`),
  // Google Search Console
  initiateGSCConnection: (projectId) => axios.post(`${API}/dfy/projects/${projectId}/gsc/connect`),
  handleGSCCallback: (projectId, sessionId) => axios.post(`${API}/dfy/projects/${projectId}/gsc/callback?session_id=${sessionId}`),
  getGSCData: (projectId) => axios.get(`${API}/dfy/projects/${projectId}/gsc/data`),
  
  // Team Management
  getTeamMembers: () => axios.get(`${API}/team/members`),
  addTeamMember: (data) => axios.post(`${API}/team/members`, data),
  updateTeamMember: (memberId, data) => axios.put(`${API}/team/members/${memberId}`, data),
  removeTeamMember: (memberId) => axios.delete(`${API}/team/members/${memberId}`),
  getMyPermissions: () => axios.get(`${API}/team/permissions`),
  getTeamActivity: (limit) => axios.get(`${API}/team/activity`, { params: { limit } }),
  resetMemberPassword: (memberId, newPassword) => axios.post(`${API}/team/members/${memberId}/reset-password?new_password=${encodeURIComponent(newPassword)}`),
  impersonateOrg: (orgId) => axios.post(`${API}/team/impersonate/${orgId}`),
};
